import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import useKeyboardInset from "../hooks/useKeyboardInset";
import GiveawayWinnerBanner from "../components/GiveawayWinnerBanner";
// AI chat assistant hidden for now

interface MainLayoutProps {
  children?: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const location = useLocation();

  // Keeps the bottom of a page (e.g. pagination) reachable while the
  // on-screen keyboard covers it — see the hook and .app-shell-scroll.
  useKeyboardInset();

  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.key]);

  // The app shell scrolls inside <main>, so the body must not scroll. Locking
  // body overflow here (not in CSS globally) keeps auth/public pages — which
  // scroll the body — unaffected, and stops Stripe's offscreen fixed iframes
  // from adding phantom scroll space below the footer.
  useEffect(() => {
    document.documentElement.classList.add("app-shell-locked");
    document.body.classList.add("app-shell-locked");
    window.scrollTo(0, 0);

    const lockWindowScroll = () => {
      // Not while a field is focused. On iOS Safari the browser scrolls the
      // window itself to lift a focused input clear of the keyboard, and
      // snapping straight back to 0 undid exactly that — so the keyboard
      // covered the lower half of a form and nothing could bring it back.
      // The lock still does its real job (absorbing the phantom scroll space
      // Stripe's offscreen iframes add) the rest of the time.
      const active = document.activeElement;
      const isEditing =
        !!active &&
        (active.tagName === "INPUT" ||
          active.tagName === "TEXTAREA" ||
          active.tagName === "SELECT" ||
          (active as HTMLElement).isContentEditable);
      if (isEditing) return;

      if (window.scrollY !== 0 || window.scrollX !== 0) {
        window.scrollTo(0, 0);
      }
    };
    window.addEventListener("scroll", lockWindowScroll, { passive: true });

    return () => {
      document.documentElement.classList.remove("app-shell-locked");
      document.body.classList.remove("app-shell-locked");
      window.removeEventListener("scroll", lockWindowScroll);
    };
  }, []);

  return (
    <>
      <GiveawayWinnerBanner />
    <div
      className="app-shell flex overflow-hidden bg-[#F5F5F5]"
      style={{ backgroundColor: "#F5F5F5", marginTop: "var(--giveaway-banner-h)" }}
    >
      <Sidebar
        isMobileOpen={isMobileSidebarOpen}
        onCloseMobile={() => setIsMobileSidebarOpen(false)}
      />
      <div className="app-shell flex-1 min-w-0 flex flex-col md:ml-[var(--sidebar-w)] transition-[margin] duration-300">
        <Header onToggleSidebar={() => setIsMobileSidebarOpen((prev) => !prev)} />
        {/* main is the ONLY scroll container. The footer flows right after
            content instead of being pinned to the bottom, so short pages
            don't show empty space between content and footer. */}
        <main className="app-shell-scroll flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col">
          <div className="p-4 md:p-6 flex-1 flex flex-col">{children}</div>
          <Footer />
        </main>
      </div>
      {/* AI chat assistant hidden for now */}
      {/* <button
        type="button"
        onClick={() => setIsAiOpen(true)}
        className="fixed -bottom-4 -right-4 z-50 cursor-pointer hover:scale-110 transition-transform m-3"
      >
        <img
          src={AiImg}
          alt="AI Assistant"
          className="w-32 h-32 object-contain drop-shadow-lg"
        />
      </button>
      <AiAssistantModal isOpen={isAiOpen} onClose={() => setIsAiOpen(false)} /> */}
    </div>
    </>
  );
};

export default MainLayout;
